import { Link, useNavigate } from 'react-router-dom'
import { AlertTriangle, CreditCard } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useBillingStore } from '@/stores/useBillingStore'
import { useBudgetAlertsStore } from '@/stores/useBudgetAlertsStore'
import { AccountMenu } from '@/components/layout/AccountMenu'
import { ThemeToggle } from '@/components/theme-toggle'
import { WorkButton } from '@/components/layout/WorkButton'
import { NotificationBadge, NotificationsBell } from '@/components/layout/NotificationsBell'

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount)
}

/** The top bar's right-hand cluster, shared by the page header and the chat bar. `account` is
 * the phone's account icon (a wide screen has it in the sidebar). */
export function HeaderActions({ account = true }: { account?: boolean }) {
  const navigate = useNavigate()

  const creditBalance = useBillingStore((state) => state.creditBalance)

  const { alerts, markAlertAsRead, markAllAlertsAsRead, getUnreadCount } = useBudgetAlertsStore()

  const unreadCount = getUnreadCount()

  return (
    <div className="flex shrink-0 items-center gap-0.5 md:gap-2" data-testid="header-actions">
      <div className="hidden md:block">
        <ThemeToggle />
      </div>

      {/* Slots stay reserved so the icon cluster never shifts when work starts. */}
      <div
        className="flex size-11 shrink-0 items-center justify-center md:size-9"
        data-testid="work-button-slot"
        data-tour="work-button"
      >
        <WorkButton />
      </div>

      {/* Always visible, phones included: the balance is one of the essentials. */}
      <div className="flex justify-end md:min-w-[6.5rem]" data-testid="credit-balance-slot">
        {creditBalance && (
          <Link
            to="/settings/plan#plan"
            aria-label={`${creditBalance.balance.toLocaleString()} credits`}
            className="text-muted-foreground hover:text-foreground flex min-h-11 min-w-11 items-center justify-center gap-1 rounded-md px-1.5 text-xs font-medium transition-colors md:min-h-0 md:min-w-0 md:px-2 md:py-1"
            data-testid="credit-balance-indicator"
            data-tour="credit-balance"
          >
            <CreditCard className="size-3.5" />
            <span>
              {creditBalance.balance.toLocaleString()}
              <span className="hidden md:inline"> credits</span>
            </span>
          </Link>
        )}
      </div>

      <NotificationsBell />

      {/* A phone has no width to hold an empty slot; it shows only while there are alerts. */}
      <div
        className={cn(
          'flex size-11 shrink-0 items-center justify-center max-[360px]:hidden md:size-9',
          unreadCount === 0 && 'max-md:hidden'
        )}
        data-testid="budget-alerts-slot"
      >
        {unreadCount > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                aria-label="Budget Alerts"
                className="relative size-11 md:size-9"
                data-testid="budget-alerts-bell"
              >
                <AlertTriangle className="text-warning size-5" />
                <NotificationBadge count={unreadCount} testId="budget-alert-count" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-80">
              <DropdownMenuLabel className="flex items-center justify-between">
                <span>Budget Alerts</span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-auto px-2 py-1 text-xs"
                  onClick={(e) => {
                    e.preventDefault()
                    markAllAlertsAsRead()
                  }}
                >
                  Mark all read
                </Button>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <div className="max-h-72 overflow-y-auto">
                {alerts.map((alert) => (
                  <DropdownMenuItem
                    key={alert.id}
                    className="cursor-pointer flex-col items-start gap-1 px-3 py-2"
                    onClick={() => {
                      markAlertAsRead(alert.id)
                      navigate('/ads/settings')
                    }}
                  >
                    <div className="flex w-full items-start justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <AlertTriangle
                          className={`h-4 w-4 shrink-0 ${
                            alert.threshold >= 100
                              ? 'text-destructive'
                              : alert.threshold >= 80
                                ? 'text-warning'
                                : 'text-warning'
                          }`}
                        />
                        <span className="text-sm font-medium">{alert.campaignName}</span>
                      </div>
                      <Badge
                        variant={
                          alert.threshold >= 100
                            ? 'destructive'
                            : alert.threshold >= 80
                              ? 'default'
                              : 'secondary'
                        }
                        className="text-xs"
                      >
                        {alert.threshold}%
                      </Badge>
                    </div>
                    <p className="text-muted-foreground w-full text-xs">
                      {alert.currentSpend !== null && alert.percentage !== null
                        ? `${formatCurrency(alert.currentSpend)} / ${formatCurrency(alert.budget)} (${Math.round(alert.percentage)}% spent)`
                        : alert.message}
                    </p>
                    {!alert.read && (
                      <span className="bg-primary absolute top-2 right-2 h-2 w-2 rounded-full" />
                    )}
                  </DropdownMenuItem>
                ))}
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="justify-center text-sm font-medium"
                onClick={() => navigate('/ads/settings')}
              >
                View all alerts & settings
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      {account && (
        <div className="md:hidden">
          <AccountMenu variant="icon" />
        </div>
      )}
    </div>
  )
}
